import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup,
  Badge, Card, ListGroup
} from 'react-bootstrap';
import {
  Save, X, User, Shirt, Calendar, DollarSign, Ruler,
  Search, AlertCircle, Clock, Package,
  Scissors,
  ImageIcon,
  Upload,
  Check
} from 'lucide-react';

import customOrderService from '../../../services/customOrderService';
import customerService from '../../../services/customerService';
import modelService from '../../../services/modelService';
import measurementService from '../../../services/measurementService';

/**
 * ✅ ATELIER: Modal de création d'une commande atelier.
 * 
 * Workflow :
 *  1. Choisir une cliente (recherche ou sélection dans la liste)
 *  2. Choisir une fiche de mesures (parmi celles de la cliente)
 *  3. Choisir un modèle (catalogue) OU saisir un nom libre
 *  4. Définir tissu, prix, date de livraison, priorité
 *  5. Enregistrer une avance initiale (optionnel)
 */
const CreateOrderModal = ({ show, onHide, onSuccess, preselectedCustomer = null }) => {

  // ===== ÉTATS =====

  const [formData, setFormData] = useState({
    customerId: '',
    measurementId: '',
    modelIds: [],
    useCustomModel: false,
    customModelName: '',
    customModelImageUrl: '',
    fabricDescription: '',
    fabricProvidedByCustomer: true,
    totalPrice: '',
    expectedDeliveryDate: '',
    priority: 'NORMAL',
    notes: '',
    discountType: 'PERCENTAGE', // ou 'FIXED'
    discountValue: '',
    // Avance initiale
    hasInitialAdvance: false,
    initialAdvanceAmount: '',
    initialAdvancePaymentMethod: 'CASH',
    initialAdvanceReference: ''
  });

  // ✅ MULTI-TISSUS : plusieurs photos (upload différé après création)
  const [fabricImageFiles, setFabricImageFiles] = useState([]);
  const [fabricImagePreviews, setFabricImagePreviews] = useState([]);
  const fabricInputRef = useRef(null);
  const [uploadingFabric, setUploadingFabric] = useState(false);

  // ✅ MULTI-MODÈLES : recherche dans le catalogue + suivi du prix
  const [modelSearch, setModelSearch] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);

  const MAX_FABRIC_PHOTOS = 6;


  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);

  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modelCategory, setModelCategory] = useState('ALL');

  // ===== CHARGEMENT INITIAL =====

  useEffect(() => {
    if (show) {
      loadCustomers();
      loadModels();
      resetForm(preselectedCustomer);
    }
  }, [show, preselectedCustomer]);

  // Charger les mesures quand la cliente change
  useEffect(() => {
    if (formData.customerId) {
      loadCustomerMeasurements(formData.customerId);
    } else {
      setMeasurements([]);
    }
  }, [formData.customerId]);

  const resetForm = (customer = null) => {
    const defaultDeliveryDate = new Date();
    defaultDeliveryDate.setDate(defaultDeliveryDate.getDate() + 7);
    const defaultDateStr = defaultDeliveryDate.toISOString().split('T')[0];

    setFormData({
      customerId: customer?.id || '',
      measurementId: '',
      modelIds: [],
      useCustomModel: false,
      customModelName: '',
      customModelImageUrl: '',
      fabricDescription: '',
      fabricProvidedByCustomer: true,
      totalPrice: '',
      expectedDeliveryDate: defaultDateStr,
      priority: 'NORMAL',
      notes: '',
      discountType: 'PERCENTAGE',  // ✅ AJOUT
      discountValue: '',           // ✅ AJOUT
      hasInitialAdvance: false,
      initialAdvanceAmount: '',
      initialAdvancePaymentMethod: 'CASH',
      initialAdvanceReference: ''
    });
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setModelSearch('');
    setModelCategory('ALL');
    setPriceTouched(false);
    setFabricImageFiles([]);
    setFabricImagePreviews([]);
    if (fabricInputRef.current) fabricInputRef.current.value = '';
    setError(null);
  };

  const loadCustomers = async () => {
    try {
      const response = await customerService.getCustomers();
      if (response.success) {
        const list = (response.data || []).map(c =>
          customerService.formatCustomerForDisplay(c)
        );
        setCustomers(list);
      }
    } catch (err) {
      console.error('Erreur chargement clientes:', err);
    }
  };

  const loadModels = async () => {
    try {
      setLoadingModels(true);
      const response = await modelService.getModels();
      if (response.success) {
        setModels(response.data || []);
      }
    } finally {
      setLoadingModels(false);
    }
  };

  const loadCustomerMeasurements = async (customerId) => {
    try {
      setLoadingMeasurements(true);
      const response = await measurementService.getMeasurementsByCustomer(customerId);
      if (response.success) {
        setMeasurements(response.data || []);
        // Auto-sélectionner la fiche la plus récente
        if (response.data && response.data.length > 0) {
          setFormData(prev => ({ ...prev, measurementId: response.data[0].id }));
        }
      }
    } finally {
      setLoadingMeasurements(false);
    }
  };

  // ===== HANDLERS =====

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleChange(field, value);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, customerId: customer.id, measurementId: '' }));
    setCustomerSearch('');
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({
      ...prev,
      customerId: '',
      measurementId: ''
    }));
    setMeasurements([]);
  };

  // Le modèle personnalisé peut désormais COEXISTER avec des modèles catalogue
  const toggleCustomModel = () => {
    setFormData(prev => ({
      ...prev,
      useCustomModel: !prev.useCustomModel,
      customModelName: prev.useCustomModel ? '' : prev.customModelName,
      customModelImageUrl: prev.useCustomModel ? '' : prev.customModelImageUrl
    }));
  };

  /**
   * ✅ MULTI-MODÈLES : cocher / décocher un modèle du catalogue.
   * Le prix total est auto-calculé (somme des prix de base) tant que
   * l'utilisateur ne l'a pas saisi manuellement.
   */
  const toggleModel = (modelId) => {
    setFormData(prev => {
      const already = prev.modelIds.includes(modelId);
      const nextIds = already
        ? prev.modelIds.filter(id => id !== modelId)
        : [...prev.modelIds, modelId];

      let nextPrice = prev.totalPrice;
      if (!priceTouched) {
        const sum = nextIds.reduce((acc, id) => {
          const m = models.find(x => x.id === id);
          return acc + (m?.basePrice ? parseFloat(m.basePrice) : 0);
        }, 0);
        nextPrice = sum > 0 ? String(sum) : '';
      }

      return { ...prev, modelIds: nextIds, totalPrice: nextPrice };
    });
  };

  const selectedModels = models.filter(m => formData.modelIds.includes(m.id));

  // Catégories disponibles pour le filtre rapide
  const modelCategories = [...new Set(
    models.map(m => m.category).filter(Boolean)
  )].sort();

  const filteredModels = models.filter(m => {
    if (modelCategory !== 'ALL' && m.category !== modelCategory) return false;
    if (!modelSearch.trim()) return true;
    const q = modelSearch.trim().toLowerCase();
    return m.name?.toLowerCase().includes(q)
      || m.category?.toLowerCase().includes(q);
  });

  const clearModels = () => {
    setFormData(prev => ({ ...prev, modelIds: [] }));
    if (!priceTouched) {
      setFormData(prev => ({ ...prev, totalPrice: '' }));
    }
  };

  // ✅ MULTI-TISSUS : sélection de plusieurs fichiers (cumulatif)
  const handleFabricImageChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    const room = MAX_FABRIC_PHOTOS - fabricImageFiles.length;
    if (room <= 0) {
      setError(`Maximum ${MAX_FABRIC_PHOTOS} photos de tissu`);
      if (fabricInputRef.current) fabricInputRef.current.value = '';
      return;
    }

    const accepted = [];
    for (const file of picked.slice(0, room)) {
      const validation = customOrderService.validateImageFile(file);
      if (!validation.isValid) {
        setError(`${file.name} : ${validation.message}`);
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length === 0) {
      if (fabricInputRef.current) fabricInputRef.current.value = '';
      return;
    }

    if (picked.length > room) {
      setError(`Seules ${room} photo(s) ont été ajoutées (maximum ${MAX_FABRIC_PHOTOS})`);
    } else {
      setError(null);
    }

    setFabricImageFiles(prev => [...prev, ...accepted]);

    accepted.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFabricImagePreviews(prev => [...prev, { name: file.name, src: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });

    if (fabricInputRef.current) fabricInputRef.current.value = '';
  };

  // ✅ MULTI-TISSUS : retirer une photo en attente
  const removeFabricImageAt = (index) => {
    setFabricImageFiles(prev => prev.filter((_, i) => i !== index));
    setFabricImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // ===== VALIDATION =====

  const validate = () => {
    if (!formData.customerId) {
      return 'Veuillez sélectionner une cliente';
    }

    if (formData.useCustomModel && !formData.customModelName.trim()) {
      return 'Veuillez saisir le nom du modèle personnalisé';
    }

    if (formData.modelIds.length === 0 && !formData.customModelName.trim()) {
      return 'Veuillez choisir au moins un modèle du catalogue (ou ajouter un modèle personnalisé)';
    }

    if (!formData.totalPrice || parseFloat(formData.totalPrice) <= 0) {
      return 'Veuillez saisir un prix total valide';
    }

    if (!formData.expectedDeliveryDate) {
      return 'Veuillez choisir une date de livraison prévue';
    }

    // Date de livraison dans le futur
    const deliveryDate = new Date(formData.expectedDeliveryDate);
    deliveryDate.setHours(23, 59, 59);
    if (deliveryDate < new Date()) {
      return 'La date de livraison doit être aujourd\'hui ou dans le futur';
    }

    // Avance initiale
    if (formData.hasInitialAdvance) {
      const advance = parseFloat(formData.initialAdvanceAmount);
      if (!advance || advance <= 0) {
        return 'Le montant de l\'avance est invalide';
      }
      if (advance > parseFloat(formData.totalPrice)) {
        return 'L\'avance ne peut pas dépasser le prix total';
      }
      if (!formData.initialAdvancePaymentMethod) {
        return 'La méthode de paiement pour l\'avance est obligatoire';
      }
    }

    // Validation remise
if (formData.discountValue && parseFloat(formData.discountValue) > 0) {
  const discount = parseFloat(formData.discountValue);
  const total = parseFloat(formData.totalPrice);

  if (formData.discountType === 'PERCENTAGE' && discount > 100) {
    return 'La remise en pourcentage ne peut pas dépasser 100%';
  }

  if (formData.discountType === 'FIXED' && discount >= total) {
    return 'La remise ne peut pas dépasser ou égaler le prix total';
  }
}

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Construire le payload
      const payload = {
        customerId: formData.customerId,
        measurementId: formData.measurementId || null,
        fabricDescription: formData.fabricDescription,
        fabricProvidedByCustomer: formData.fabricProvidedByCustomer,
        totalPrice: formData.totalPrice,
        expectedDeliveryDate: formData.expectedDeliveryDate,
        priority: formData.priority,
        notes: formData.notes
        
      };

      // ✅ MULTI-MODÈLES : catalogue ET/OU modèle libre
      payload.modelIds = formData.modelIds;
      if (formData.useCustomModel && formData.customModelName.trim()) {
        payload.customModelName = formData.customModelName;
        payload.customModelImageUrl = formData.customModelImageUrl;
      }

      // Avance initiale
      if (formData.hasInitialAdvance) {
        payload.initialAdvanceAmount = formData.initialAdvanceAmount;
        payload.initialAdvancePaymentMethod = formData.initialAdvancePaymentMethod;
        payload.initialAdvanceReference = formData.initialAdvanceReference;
      }

      // ✅ Remise (optionnelle)
  if (formData.discountValue && parseFloat(formData.discountValue) > 0) {
    payload.discountType = formData.discountType;
    payload.discountValue = formData.discountValue;
    console.log('💰 Remise dans payload:', formData.discountType, formData.discountValue);
  }

      const response = await customOrderService.createOrder(payload);

      if (response.success) {
        console.log('✅ Commande créée:', response.data);
        const createdOrder = response.data;

        // ✅ MULTI-TISSUS : upload de toutes les photos sélectionnées
        if (fabricImageFiles.length > 0 && createdOrder?.id) {
          setUploadingFabric(true);
          try {
            const uploadRes = await customOrderService.uploadFabricImages(
              createdOrder.id,
              fabricImageFiles
            );
            createdOrder.fabricImageUrls = uploadRes.urls;
            createdOrder.fabricImageUrl = uploadRes.urls[0] || null;

            if (!uploadRes.success) {
              // La commande est créée : on ne bloque pas, l'utilisateur pourra
              // réessayer depuis OrderDetailModal.
              console.warn('⚠️ Commande créée mais upload partiel:', uploadRes.message);
            }
          } catch (uploadErr) {
            console.error('❌ Erreur upload tissus:', uploadErr);
          } finally {
            setUploadingFabric(false);
          }
        }

        onSuccess?.(createdOrder);
        onHide();
      } else {
        setError(response.message || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  // ===== FILTRAGE CLIENTES =====

  const filteredCustomers = customerSearch.trim()
    ? customers.filter(c => {
        const q = customerSearch.trim().toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone && c.phone.includes(q))
        );
      })
    : customers;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <Shirt size={20} />
          Nouvelle commande atelier
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          {/* ========== ÉTAPE 1 : CLIENTE ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <User size={18} />
              1. Cliente
            </h6>

            {selectedCustomer ? (
              <Card className="bg-primary bg-opacity-10 border-primary">
                <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{selectedCustomer.name}</strong>
                    {selectedCustomer.phone && (
                      <div className="small text-muted">📞 {selectedCustomer.phone}</div>
                    )}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleClearCustomer}
                  >
                    <X size={14} /> Changer
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <div>
                <InputGroup className="mb-2">
                  <InputGroup.Text><Search size={14} /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par nom ou téléphone..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                  />
                </InputGroup>

                <div style={{ maxHeight: '180px', overflowY: 'auto' }} className="border rounded">
                  {filteredCustomers.length === 0 ? (
                    <div className="text-center p-3 text-muted small">
                      Aucune cliente trouvée
                    </div>
                  ) : (
                    <ListGroup variant="flush">
                      {filteredCustomers.slice(0, 20).map(c => (
                        <ListGroup.Item
                          key={c.id}
                          action
                          onClick={() => handleSelectCustomer(c)}
                          className="d-flex justify-content-between align-items-center py-2"
                        >
                          <div>
                            <strong>{c.name}</strong>
                            {c.phone && (
                              <small className="text-muted ms-2">{c.phone}</small>
                            )}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ========== ÉTAPE 2 : MESURES ========== */}
          {formData.customerId && (
            <div className="mb-4">
              <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
                <Ruler size={18} />
                2. Fiche de mesures
                <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>
                  Optionnel
                </Badge>
              </h6>

              {loadingMeasurements ? (
                <div className="text-center py-2">
                  <Spinner size="sm" /> Chargement...
                </div>
              ) : measurements.length === 0 ? (
                <Alert variant="warning" className="small mb-0">
                  Cette cliente n'a pas de fiche de mesures. Vous pouvez créer la commande sans,
                  mais c'est recommandé d'en créer une.
                </Alert>
              ) : (
                <Form.Select
                  value={formData.measurementId}
                  onChange={(e) => handleChange('measurementId', e.target.value)}
                >
                  <option value="">Aucune fiche</option>
                  {measurements.map(m => (
                    <option key={m.id} value={m.id}>
                      Fiche du {customOrderService.formatDate(m.createdAt)} —{' '}
                      {m.totalMeasurementsCount} mesures
                    </option>
                  ))}
                </Form.Select>
              )}
            </div>
          )}

          {/* ========== ÉTAPE 3 : MODÈLES ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <Shirt size={18} />
              3. Modèles
              {formData.modelIds.length > 0 && (
                <Badge bg="primary">{formData.modelIds.length}</Badge>
              )}
            </h6>

            {loadingModels ? (
              <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : models.length === 0 ? (
              <Alert variant="warning" className="small">
                Aucun modèle dans le catalogue. Ajoutez un modèle personnalisé ci-dessous.
              </Alert>
            ) : (
              <>
                {/* Modèles sélectionnés */}
                {selectedModels.length > 0 && (
                  <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
                    {selectedModels.map(m => (
                      <Badge
                        bg="primary"
                        key={m.id}
                        className="d-flex align-items-center gap-1 py-2"
                      >
                        {m.name}
                        <X
                          size={12}
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleModel(m.id)}
                        />
                      </Badge>
                    ))}
                    <Button
                      variant="link"
                      size="sm"
                      className="text-muted p-0 ms-1"
                      onClick={clearModels}
                      type="button"
                    >
                      Tout retirer
                    </Button>
                  </div>
                )}

                {/* Recherche */}
                <InputGroup size="sm" className="mb-2">
                  <InputGroup.Text><Search size={14} /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher un modèle..."
                    value={modelSearch}
                    onChange={(e) => setModelSearch(e.target.value)}
                  />
                  {modelSearch && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setModelSearch('')}
                      type="button"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </InputGroup>

                {/* Filtre par catégorie */}
                {modelCategories.length > 1 && (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    <Badge
                      bg={modelCategory === 'ALL' ? 'secondary' : 'light'}
                      text={modelCategory === 'ALL' ? 'white' : 'dark'}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setModelCategory('ALL')}
                    >
                      Toutes ({models.length})
                    </Badge>
                    {modelCategories.map(cat => (
                      <Badge
                        key={cat}
                        bg={modelCategory === cat ? 'secondary' : 'light'}
                        text={modelCategory === cat ? 'white' : 'dark'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setModelCategory(cat)}
                      >
                        {cat}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Grille scrollable */}
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                >
                  {filteredModels.length === 0 ? (
                    <div className="text-center text-muted py-3 small">
                      Aucun modèle ne correspond
                    </div>
                  ) : (
                    <Row className="g-2">
                      {filteredModels.map(m => {
                        const isSelected = formData.modelIds.includes(m.id);
                        const thumb = modelService.buildImageUrl(m.imageUrl);
                        return (
                          <Col xs={6} sm={4} key={m.id}>
                            <div
                              className={`model-pick ${isSelected ? 'model-pick-selected' : ''}`}
                              onClick={() => toggleModel(m.id)}
                              role="button"
                            >
                              <div className="model-pick-thumb">
                                {thumb ? (
                                  <img
                                    src={thumb}
                                    alt={m.name}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <Shirt size={22} className="text-muted" />
                                )}
                                {isSelected && (
                                  <span className="model-pick-check">
                                    <Check size={11} strokeWidth={3} />
                                  </span>
                                )}
                              </div>
                              <div className="p-1">
                                <div className="model-pick-name" title={m.name}>
                                  {m.name}
                                </div>
                                {m.basePrice > 0 && (
                                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                    {modelService.formatPrice(m.basePrice)}
                                  </small>
                                )}
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>

                <Form.Text className="text-muted">
                  {filteredModels.length} modèle(s) affiché(s) sur {models.length}
                  {' · '}Cliquez pour sélectionner
                </Form.Text>
              </>
            )}

            <Form.Check
              type="switch"
              id="custom-model-switch"
              label="Ajouter aussi un modèle personnalisé (hors catalogue)"
              checked={formData.useCustomModel}
              onChange={toggleCustomModel}
              className="mt-3 mb-2"
            />

            {formData.useCustomModel && (
              <Row className="g-2">
                <Col md={12}>
                  <Form.Control
                    type="text"
                    placeholder="Nom du modèle (ex: Robe inspiration Instagram)"
                    value={formData.customModelName}
                    onChange={(e) => handleChange('customModelName', e.target.value)}
                  />
                </Col>
                <Col md={12}>
                  <Form.Control
                    type="text"
                    placeholder="URL de l'image d'inspiration (optionnel)"
                    value={formData.customModelImageUrl}
                    onChange={(e) => handleChange('customModelImageUrl', e.target.value)}
                  />
                </Col>
              </Row>
            )}

            <style>{`
              .model-pick {
                border: 2px solid #dee2e6;
                border-radius: 8px;
                overflow: hidden;
                cursor: pointer;
                transition: border-color .15s, box-shadow .15s;
                background: #fff;
                height: 100%;
              }
              .model-pick:hover {
                border-color: #86b7fe;
              }
              .model-pick-selected {
                border-color: #0d6efd;
                box-shadow: 0 0 0 2px rgba(13,110,253,.15);
              }
              .model-pick-thumb {
                position: relative;
                height: 80px;
                background: #f8f9fa;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
              }
              .model-pick-thumb img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
              .model-pick-check {
                position: absolute;
                top: 4px;
                right: 4px;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background: #0d6efd;
                color: #fff;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .model-pick-name {
                font-size: .78rem;
                font-weight: 600;
                line-height: 1.2;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                min-height: 1.9em;
              }
            `}</style>
          </div>

          {/* ========== ÉTAPE 4 : TISSU ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <Package size={18} />
              4. Tissu
            </h6>

            {/* ✅ MULTI-TISSUS : plusieurs photos */}
            <Form.Group className="mb-3 mt-3">
              <Form.Label className="d-flex align-items-center gap-2">
                <Scissors size={14} className="text-success" />
                Photos des tissus (optionnel)
                {fabricImageFiles.length > 0 && (
                  <Badge bg="success">{fabricImageFiles.length}</Badge>
                )}
              </Form.Label>

              <div className="p-3 border rounded bg-light">
                {fabricImagePreviews.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {fabricImagePreviews.map((preview, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={preview.src}
                          alt={`Tissu ${index + 1}`}
                          className="img-thumbnail"
                          style={{
                            width: '96px',
                            height: '96px',
                            objectFit: 'cover'
                          }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: '20px',
                            height: '20px',
                            transform: 'translate(30%, -30%)'
                          }}
                          onClick={() => removeFabricImageAt(index)}
                          type="button"
                        >
                          <X size={11} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-3 text-center">
                    <ImageIcon size={36} className="text-muted mb-2" />
                    <p className="text-muted mb-0 small">Aucune photo</p>
                  </div>
                )}

                <div className="mt-3 text-center">
                  <input
                    ref={fabricInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFabricImageChange}
                    style={{ display: 'none' }}
                    id="orderFabricInput"
                  />
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => fabricInputRef.current?.click()}
                    type="button"
                    disabled={loading || uploadingFabric
                      || fabricImageFiles.length >= MAX_FABRIC_PHOTOS}
                  >
                    <Upload size={14} className="me-1" />
                    {fabricImageFiles.length > 0 ? 'Ajouter d\'autres photos' : 'Sélectionner'}
                  </Button>
                </div>
              </div>

              <Form.Text className="text-muted">
                Un tissu par modèle si besoin · JPG, PNG, WebP · Max 5MB chacune ·
                Jusqu'à {MAX_FABRIC_PHOTOS} photos
              </Form.Text>
            </Form.Group>

            <Form.Check
              type="switch"
              id="fabric-by-customer"
              label="Tissu fourni par la cliente"
              checked={formData.fabricProvidedByCustomer}
              onChange={(e) => handleChange('fabricProvidedByCustomer', e.target.checked)}
            />

            {/* ✅ MULTI-TISSUS : plusieurs photos */}
            <Form.Group className="mb-3 mt-3">
              <Form.Label className="d-flex align-items-center gap-2">
                <Scissors size={14} className="text-success" />
                Photos des tissus (optionnel)
                {fabricImageFiles.length > 0 && (
                  <Badge bg="success">{fabricImageFiles.length}</Badge>
                )}
              </Form.Label>

              <div className="p-3 border rounded bg-light">
                {fabricImagePreviews.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {fabricImagePreviews.map((preview, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={preview.src}
                          alt={`Tissu ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: '96px', height: '96px', objectFit: 'cover' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                          style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }}
                          onClick={() => removeFabricImageAt(index)}
                          type="button"
                        >
                          <X size={11} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-3 text-center">
                    <ImageIcon size={36} className="text-muted mb-2" />
                    <p className="text-muted mb-0 small">Aucune photo</p>
                  </div>
                )}

                <div className="mt-3 text-center">
                  <input
                    ref={fabricInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFabricImageChange}
                    style={{ display: 'none' }}
                    id="orderFabricInput"
                  />
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => fabricInputRef.current?.click()}
                    type="button"
                    disabled={loading || uploadingFabric
                      || fabricImageFiles.length >= MAX_FABRIC_PHOTOS}
                  >
                    <Upload size={14} className="me-1" />
                    {fabricImageFiles.length > 0 ? 'Ajouter d\'autres photos' : 'Sélectionner'}
                  </Button>
                </div>
              </div>

              <Form.Text className="text-muted">
                Un tissu par modèle si besoin · JPG, PNG, WebP · Max 5MB chacune ·
                Jusqu'à {MAX_FABRIC_PHOTOS} photos
              </Form.Text>
            </Form.Group>
          </div>

          {/* ========== ÉTAPE 5 : PRIX & LIVRAISON ========== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
              <DollarSign size={18} />
              5. Prix et livraison
            </h6>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">
                    Prix total <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={formData.totalPrice}
                      onChange={(e) => {
                        setPriceTouched(true);
                        handleNumberChange('totalPrice', e.target.value);
                      }}
                    />
                    <InputGroup.Text>FCFA</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">
                    <Calendar size={12} className="me-1" />
                    Livraison prévue <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.expectedDeliveryDate}
                    onChange={(e) => handleChange('expectedDeliveryDate', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">
                    <Clock size={12} className="me-1" />
                    Priorité
                  </Form.Label>
                  <Form.Select
                    value={formData.priority}
                    onChange={(e) => handleChange('priority', e.target.value)}
                  >
                    <option value="NORMAL">🔵 Normal</option>
                    <option value="URGENT">🔴 Urgent</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>
          {/* ========== ÉTAPE 5.5 : REMISE (OPTIONNELLE) ========== */}
<div className="mb-4">
  <h6 className="d-flex align-items-center gap-2 text-primary mb-3">
    <DollarSign size={18} />
    5.5 Remise (optionnel)
    <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>
      Optionnel
    </Badge>
  </h6>

  <Row className="g-3">
    <Col md={6}>
      <Form.Group>
        <Form.Label className="small">Type de remise</Form.Label>
        <Form.Select
          value={formData.discountType}
          onChange={(e) => handleChange('discountType', e.target.value)}
        >
          <option value="PERCENTAGE">Pourcentage (%)</option>
          <option value="FIXED">Montant fixe (FCFA)</option>
        </Form.Select>
      </Form.Group>
    </Col>

    <Col md={6}>
      <Form.Group>
        <Form.Label className="small">
          Valeur de la remise
        </Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={formData.discountValue}
            onChange={(e) => handleNumberChange('discountValue', e.target.value)}
          />
          <InputGroup.Text>
            {formData.discountType === 'PERCENTAGE' ? '%' : 'FCFA'}
          </InputGroup.Text>
        </InputGroup>
      </Form.Group>
    </Col>

    {/* Aperçu de la remise calculée */}
    {formData.discountValue && parseFloat(formData.discountValue) > 0 
     && formData.totalPrice && parseFloat(formData.totalPrice) > 0 && (
      <Col md={12}>
        <Card className="bg-success bg-opacity-10 border-success">
          <Card.Body className="py-2 px-3">
            {(() => {
              const total = parseFloat(formData.totalPrice);
              const discountVal = parseFloat(formData.discountValue);
              let discountAmount = 0;

              if (formData.discountType === 'PERCENTAGE') {
                discountAmount = (total * discountVal) / 100;
              } else {
                discountAmount = discountVal;
              }

              const finalPrice = total - discountAmount;

              return (
                <Row className="g-2 small">
                  <Col xs={6}>
                    <div className="text-muted">Sous-total :</div>
                    <strong>{total.toLocaleString('fr-FR')} FCFA</strong>
                  </Col>
                  <Col xs={6}>
                    <div className="text-muted">Remise :</div>
                    <strong className="text-danger">
                      -{discountAmount.toLocaleString('fr-FR')} FCFA
                    </strong>
                  </Col>
                  <Col xs={12} className="border-top pt-2 mt-1">
                    <div className="text-muted">Total final :</div>
                    <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                      {finalPrice.toLocaleString('fr-FR')} FCFA
                    </strong>
                  </Col>
                </Row>
              );
            })()}
          </Card.Body>
        </Card>
      </Col>
    )}
  </Row>
</div>

          {/* ========== ÉTAPE 6 : AVANCE INITIALE ========== */}
          <div className="mb-4">
            <Form.Check
              type="switch"
              id="advance-switch"
              label={
                <span className="fw-semibold">
                  <DollarSign size={14} className="me-1" />
                  Enregistrer une avance initiale
                </span>
              }
              checked={formData.hasInitialAdvance}
              onChange={(e) => handleChange('hasInitialAdvance', e.target.checked)}
              className="mb-2"
            />

            {formData.hasInitialAdvance && (
              <Card className="bg-success bg-opacity-10 border-success">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="small">
                        Montant avance <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup size="sm">
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          placeholder="0"
                          value={formData.initialAdvanceAmount}
                          onChange={(e) => handleNumberChange('initialAdvanceAmount', e.target.value)}
                        />
                        <InputGroup.Text>FCFA</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small">Méthode de paiement <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        size="sm"
                        value={formData.initialAdvancePaymentMethod}
                        onChange={(e) => handleChange('initialAdvancePaymentMethod', e.target.value)}
                      >
                        <option value="CASH">💵 Espèces</option>
                        <option value="WAVE">📱 Wave</option>
                        <option value="ORANGE_MONEY">🟠 Orange Money</option>
                        <option value="FREE_MONEY">🟡 Free Money</option>
                        <option value="BANK_TRANSFER">🏦 Virement</option>
                        <option value="CHECK">📄 Chèque</option>
                        <option value="CARD">💳 Carte</option>
                      </Form.Select>
                    </Col>
                    <Col md={12}>
                      <Form.Label className="small">Référence (optionnel)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Ex: WV123456"
                        value={formData.initialAdvanceReference}
                        onChange={(e) => handleChange('initialAdvanceReference', e.target.value)}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* ========== NOTES ========== */}
          <Form.Group className="mb-3">
            <Form.Label className="small">Notes (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Observations, préférences de la cliente..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            <X size={16} className="me-1" />
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            
             {loading ? (
              uploadingFabric
                ? `Upload des tissus (${fabricImageFiles.length})...`
                : 'Création...'
            ) : (
              <><Save size={16} className="me-1" /> Créer la commande</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateOrderModal;